// ** Modules
import Image from 'next/image';
import { useId } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';

export type NavGridColumns = 2 | 3 | 4;

export type NavCardModel = {
	id: string;
	headline: string;
	copy?: string;
	ctaLabel: string;
	href: string;
	imageUrl?: string;
	imageAlt?: string;
};

type CardDimensions = {
	height: number;
	imageHeight: number;
};

const MOBILE_CARD_DIMENSIONS: Record<NavGridColumns, CardDimensions> = {
	2: { height: 409, imageHeight: 188 },
	3: { height: 409, imageHeight: 188 },
	4: { height: 271, imageHeight: 144 }
};

const DESKTOP_CARD_DIMENSIONS: Record<NavGridColumns, CardDimensions> = {
	2: { height: 560, imageHeight: 280 },
	3: { height: 560, imageHeight: 280 },
	4: { height: 428, imageHeight: 208 }
};

export const NAV_GRID_DESKTOP_CARD_WIDTHS: Record<NavGridColumns, number> = {
	2: 624,
	3: 416,
	4: 308
};

export type NavCardProps = NavCardModel & {
	columns: NavGridColumns;
};

const NavCard = ({ headline, copy, ctaLabel, href, imageUrl, imageAlt, columns }: NavCardProps) => {
	const headingId = useId();
	const theme = useTheme();
	const showCopy = columns !== 4;
	const useCompactTabletTypography = columns === 3;
	const mobileDimensions = MOBILE_CARD_DIMENSIONS[columns];
	const desktopDimensions = DESKTOP_CARD_DIMENSIONS[columns];
	const desktopWidth = NAV_GRID_DESKTOP_CARD_WIDTHS[columns];
	const imageSizes =
		columns === 4
			? '(max-width: 767px) 361px, (max-width: 1199px) 308px, 308px'
			: columns === 3
				? `(max-width: 767px) 361px, (max-width: 1199px) 500px, ${desktopWidth}px`
				: `(max-width: 767px) 361px, ${desktopWidth}px`;

	return (
		<Card
			component="li"
			className="NavCard-root"
			aria-labelledby={headingId}
			sx={{
				width: '100%',
				maxWidth: { xs: 361, md: '100%' },
				height: {
					xs: mobileDimensions.height,
					md: columns === 3 ? mobileDimensions.height : desktopDimensions.height
				},
				...(columns === 3 && {
					'@media (min-width:1200px)': { height: desktopDimensions.height }
				}),
				position: 'relative',
				borderRadius: '8px',
				backgroundColor: theme.palette.heroLightBackground?.main ?? theme.palette.background.paper,
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'column',
				boxShadow: theme.shadows[1],
				transition:
					'box-shadow 400ms cubic-bezier(0.4, 0, 0.2, 1), background-color 400ms cubic-bezier(0.4, 0, 0.2, 1), border-color 400ms cubic-bezier(0.4, 0, 0.2, 1)',
				'&::after': {
					content: '""',
					position: 'absolute',
					inset: 0,
					borderRadius: 'inherit',
					border: `1px solid ${alpha(theme.palette.primary.light, 0.28)}`,
					opacity: 0,
					transition: 'opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)',
					pointerEvents: 'none'
				},
				'& .NavCard-content': {
					transition: 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)'
				},
				'@media (hover: hover) and (pointer: fine)': {
					'&:hover': {
						backgroundColor: alpha(theme.palette.common.white, 0.92),
						boxShadow: theme.shadows[3],
						'&::after': {
							opacity: 1
						},
						'& .NavCard-content': {
							transform: 'translateX(2px)'
						}
					},
					'&:hover .NavCard-imageMedia': {
						transform: 'scale(1.03) translateX(4px)'
					}
				},
				'&:focus-within': {
					backgroundColor: alpha(theme.palette.common.white, 0.92),
					boxShadow: theme.shadows[3],
					'&::after': {
						opacity: 1
					},
					'& .NavCard-content': {
						transform: 'translateX(2px)'
					}
				},
				'&:focus-within .NavCard-imageMedia': {
					transform: 'scale(1.03) translateX(4px)'
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
					'&::after': { transition: 'none' },
					'& .NavCard-content': { transition: 'none' },
					'&:hover .NavCard-imageMedia': { transform: 'none' },
					'&:focus-within .NavCard-imageMedia': { transform: 'none' }
				}
			}}
		>
			<Box
				aria-hidden={imageUrl ? undefined : true}
				sx={{
					position: 'relative',
					width: '100%',
					height: {
						xs: mobileDimensions.imageHeight,
						md: columns === 3 ? mobileDimensions.imageHeight : desktopDimensions.imageHeight
					},
					...(columns === 3 && {
						'@media (min-width:1200px)': {
							height: desktopDimensions.imageHeight
						}
					}),
					flexShrink: 0,
					backgroundColor: theme.palette.grey[200],
					overflow: 'hidden'
				}}
			>
				{imageUrl ? (
					<Image
						src={imageUrl}
						alt={imageAlt ?? ''}
						fill
						sizes={imageSizes}
						className="NavCard-imageMedia"
						style={{
							objectFit: 'cover',
							transform: 'scale(1.01) translateX(0)',
							transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)'
						}}
					/>
				) : null}
			</Box>

			<Link
				href={href}
				aria-label={`${headline} – ${ctaLabel}`}
				className="NavCard-stretchedLink"
				display="block"
				style={{
					position: 'absolute',
					inset: 0,
					zIndex: 1,
					borderRadius: 'inherit'
				}}
			/>

			<Box
				className="NavCard-content"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					flex: '1 1 auto',
					minHeight: 0,
					px: { xs: 4, md: 6 },
					pt: { xs: 6, md: 8 },
					pb: { xs: 6, md: 8 }
				}}
			>
				<Typography
					id={headingId}
					variant="title-sm"
					component="h3"
					sx={{
						color: theme.palette.common.black,
						fontSize: {
							xs: '1.125rem',
							md: useCompactTabletTypography ? '1.125rem' : '1.5rem'
						},
						fontWeight: 400,
						lineHeight: {
							xs: '1.625rem',
							md: useCompactTabletTypography ? '1.625rem' : '2rem'
						},
						letterSpacing: {
							xs: 0,
							md: useCompactTabletTypography ? 0 : '-0.05px'
						},
						flexShrink: 0,
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						...(useCompactTabletTypography && {
							'@media (min-width:1200px)': {
								fontSize: '1.5rem',
								lineHeight: '2rem',
								letterSpacing: '-0.05px'
							}
						})
					}}
				>
					{headline}
				</Typography>

				{showCopy ? (
					<Typography
						variant="body-md"
						component="p"
						sx={{
							mt: { xs: 4, md: 6 },
							color: theme.palette.text.secondary,
							fontSize: {
								xs: '1rem',
								md: useCompactTabletTypography ? '1rem' : '1.125rem'
							},
							fontWeight: 400,
							lineHeight: {
								xs: '1.375rem',
								md: useCompactTabletTypography ? '1.375rem' : '1.625rem'
							},
							letterSpacing: '-0.05px',
							display: '-webkit-box',
							WebkitLineClamp: 3,
							lineClamp: 3,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							'@media (min-width:900px)': {
								WebkitLineClamp: useCompactTabletTypography ? 3 : 4,
								lineClamp: useCompactTabletTypography ? 3 : 4
							},
							...(useCompactTabletTypography && {
								'@media (min-width:1200px)': {
									fontSize: '1.125rem',
									lineHeight: '1.625rem',
									WebkitLineClamp: 4,
									lineClamp: 4
								}
							})
						}}
					>
						{copy ?? ''}
					</Typography>
				) : null}

				<Box sx={{ mt: 'auto', pt: { xs: 8, md: 8 } }} inert aria-hidden="true">
					<UnderlineHoverCta
						href={href}
						label={ctaLabel}
						color={theme.palette.text.secondary}
						typographyVariant="body-md"
						fontSize={{
							xs: '1rem',
							md: useCompactTabletTypography ? '1rem' : '1.125rem'
						}}
						fontWeight={500}
						lineHeight={{
							xs: '1.375rem',
							md: useCompactTabletTypography ? '1.375rem' : '1.625rem'
						}}
						letterSpacing="-0.05px"
						activeOnParentHoverSelector=".NavCard-root"
						truncateLabel
						underlineThickness="1px"
						underlineBaseOpacity={0.2}
						underlineHoverOpacity={0.75}
						arrowShiftPx={2}
						paddingBottom="5px"
						underlineOffsetBottom={2}
					/>
				</Box>
			</Box>
		</Card>
	);
};

export default NavCard;
